<template>
    <div class="image-gallery">
      <!-- Gallery Controls (Optional) -->
      <div class="gallery-controls" v-if="showControls">
        <div class="control-group">
          <button 
            class="control-btn"
            :class="{ active: viewMode === 'grid' }"
            @click="viewMode = 'grid'"
            title="Grid View"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="7" rx="1" :stroke="viewMode === 'grid' ? '#007bff' : '#666'" stroke-width="2"/>
              <rect x="14" y="3" width="7" height="7" rx="1" :stroke="viewMode === 'grid' ? '#007bff' : '#666'" stroke-width="2"/>
              <rect x="3" y="14" width="7" height="7" rx="1" :stroke="viewMode === 'grid' ? '#007bff' : '#666'" stroke-width="2"/>
              <rect x="14" y="14" width="7" height="7" rx="1" :stroke="viewMode === 'grid' ? '#007bff' : '#666'" stroke-width="2"/>
            </svg>
          </button>
          
          <button 
            class="control-btn"
            :class="{ active: viewMode === 'masonry' }"
            @click="viewMode = 'masonry'"
            title="Masonry View"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="2" width="12" height="8" rx="1" :stroke="viewMode === 'masonry' ? '#007bff' : '#666'" stroke-width="2"/>
              <rect x="2" y="14" width="8" height="8" rx="1" :stroke="viewMode === 'masonry' ? '#007bff' : '#666'" stroke-width="2"/>
              <rect x="14" y="2" width="8" height="20" rx="1" :stroke="viewMode === 'masonry' ? '#007bff' : '#666'" stroke-width="2"/>
            </svg>
          </button>
        </div>
        
        <div class="control-group" v-if="images.length > 1">
          <select v-model="itemsPerRow" class="control-select">
            <option value="2">2 per row</option>
            <option value="3">3 per row</option>
            <option value="4">4 per row</option>
            <option value="5">5 per row</option>
            <option value="6">6 per row</option>
          </select>
        </div>
      </div>
  
      <!-- Image Grid -->
      <div 
        class="image-grid"
        :class="[viewMode, `cols-${itemsPerRow}`]"
        :style="gridStyle"
      >
        <div 
          v-for="(image, index) in images"
          :key="image.id || index"
          class="image-item"
          @click="openLightbox(index)"
        >
          <div class="image-container">
            <img
              :src="`/images/${image}`"
              :alt="image.alt || `Image ${index + 1}`"
              :loading="index < 6 ? 'eager' : 'lazy'"
              class="gallery-image"
              @load="handleImageLoad(index)"
              @error="handleImageError(index)"
            />
            
            <!-- Loading State -->
            <!-- <div v-if="!loadedImages[index]" class="image-loading">
              <div class="loading-spinner"></div>
            </div>
             -->
            <!-- Error State -->
            <div v-if="errorImages[index]" class="image-error">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="#dc3545">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            
            <!-- Hover Overlay -->
            <div class="image-overlay">
              <button class="view-btn" @click.stop="openLightbox(index)">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
                  <path d="M12 9c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Empty State -->
      <div v-if="images.length === 0" class="empty-state">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="#6c757d">
          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
        </svg>
        <h3>No images to display</h3>
      </div>
  
      <!-- Lightbox -->
      <transition name="fade">
        <div 
          v-if="showLightbox"
          class="lightbox"
          @click.self="closeLightbox"
        >
          <div class="lightbox-container">
            <!-- Close Button -->
            <button class="lightbox-close" @click="closeLightbox">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
            
            <!-- Navigation Buttons -->
            <button 
              class="lightbox-nav prev"
              @click="prevImage"
              :disabled="currentImageIndex === 0"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
              </svg>
            </button>
            
            <!-- Image Container -->
            <div class="lightbox-image-container">
              <img
                :src="`/images/${currentImage}`"
                :alt="currentImage.alt || `Image ${currentImageIndex + 1}`"
                class="lightbox-image"
                @load="currentImageLoaded = true"
              />
              
              <!-- Loading in Lightbox -->
              <div v-if="!currentImageLoaded" class="lightbox-loading">
                <div class="loading-spinner"></div>
              </div>
            </div>
            
            <!-- Next Button -->
            <button 
              class="lightbox-nav next"
              @click="nextImage"
              :disabled="currentImageIndex === images.length - 1"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
              </svg>
            </button>
            
            <!-- Image Counter -->
            <div class="image-counter" v-if="images.length > 1">
              {{ currentImageIndex + 1 }} / {{ images.length }}
            </div>
            
            <!-- Download Button -->
            <button class="download-btn" @click="downloadCurrentImage">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
              </svg>
              Download
            </button>
          </div>
        </div>
      </transition>
    </div>
  </template>
  
  <script>
  export default {
    name: 'ImageGallery',
    props: {
      // Accepts array of image paths or objects
      images: {
        type: Array,
        required: true,
        default: () => []
      },
      // Configuration
      config: {
        type: Object,
        default: () => ({
          showControls: true,
          initialView: 'grid',
          itemsPerRow: 3,
          gap: '20px'
        })
      }
    },
    data() {
      return {
        viewMode: this.config.initialView || 'grid',
        itemsPerRow: this.config.itemsPerRow || 3,
        showLightbox: false,
        currentImageIndex: 0,
        currentImageLoaded: false,
        loadedImages: {},
        errorImages: {},
        gap: this.config.gap || '20px'
      };
    },
    computed: {
      // Current image in lightbox
      currentImage() {
        return this.images[this.currentImageIndex] || {};
      },
      
      // Check if controls should be shown
      showControls() {
        return this.config.showControls !== false && this.images.length > 0;
      },
      
      // Grid style for masonry view
      gridStyle() {
        if (this.viewMode === 'masonry') {
          return {
            'grid-template-columns': `repeat(${this.itemsPerRow}, 1fr)`,
            'gap': this.gap
          };
        }
        return { 'gap': this.gap };
      }
    },
    methods: {
      // Get image URL - handles both string paths and objects
      getImageUrl(image) {
        if (typeof image === 'string') {
          // It's just a path string
          return this.resolveAssetPath(image);
        } else if (image && image.path) {
          // It's an object with a path property
          return this.resolveAssetPath(image.path);
        } else if (image && image.url) {
          // It's an object with a url property
          return image.url;
        }
        // Return empty string if image is invalid
        return '';
      },
      
      // Resolve asset path from assets/images/
      resolveAssetPath(path) {
        // Remove leading slash if present
        const cleanPath = path.startsWith('/') ? path.substring(1) : path;
        
        // try {
        //   // For Vue CLI/webpack
        //   return require(`@/assets/images/${cleanPath}`);
        // } catch (e) {
          // Fallback to relative path
          return `/assets/images/${cleanPath}`;
        // }
      },
      
      // Handle image load
      handleImageLoad(index) {
        this.$set(this.loadedImages, index, true);
        this.$delete(this.errorImages, index);
      },
      
      // Handle image error
      handleImageError(index) {
        this.$set(this.errorImages, index, true);
        this.$delete(this.loadedImages, index);
      },
      
      // Open lightbox
      openLightbox(index) {
        this.currentImageIndex = index;
        this.showLightbox = true;
        this.currentImageLoaded = false;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.handleKeydown);
      },
      
      // Close lightbox
      closeLightbox() {
        this.showLightbox = false;
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.handleKeydown);
      },
      
      // Navigate to next image
      nextImage() {
        if (this.currentImageIndex < this.images.length - 1) {
          this.currentImageIndex++;
          this.currentImageLoaded = false;
        }
      },
      
      // Navigate to previous image
      prevImage() {
        if (this.currentImageIndex > 0) {
          this.currentImageIndex--;
          this.currentImageLoaded = false;
        }
      },
      
      // Handle keyboard navigation
      handleKeydown(event) {
        if (!this.showLightbox) return;
        
        switch (event.key) {
          case 'Escape':
            this.closeLightbox();
            break;
          case 'ArrowLeft':
            this.prevImage();
            break;
          case 'ArrowRight':
            this.nextImage();
            break;
        }
      },
      
      // Download current image
      downloadCurrentImage() {
        const link = document.createElement('a');
        link.href = this.getImageUrl(this.currentImage);
        
        // Extract filename
        let filename = 'image';
        if (typeof this.currentImage === 'string') {
          const parts = this.currentImage.split('/');
          filename = parts[parts.length - 1];
        } else if (this.currentImage.filename) {
          filename = this.currentImage.filename;
        } else if (this.currentImage.path) {
          const parts = this.currentImage.path.split('/');
          filename = parts[parts.length - 1];
        }
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    mounted() {
      // Preload first few images
      this.images.slice(0, 4).forEach((image, index) => {
        const img = new Image();
        img.src = this.getImageUrl(image);
        img.onload = () => this.handleImageLoad(index);
        img.onerror = () => this.handleImageError(index);
      });
    }
  };
  </script>
  
  <style scoped>
  .image-gallery {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  
  /* Gallery Controls */
  .gallery-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;
  }
  
  .control-group {
    display: flex;
    gap: 12px;
    align-items: center;
  }
  
  .control-btn {
    background: white;
    border: 2px solid #dee2e6;
    border-radius: 6px;
    padding: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .control-btn:hover {
    border-color: #007bff;
    background: #f8f9fa;
  }
  
  .control-btn.active {
    border-color: #007bff;
    background: #e7f1ff;
  }
  
  .control-select {
    padding: 8px 12px;
    border: 2px solid #dee2e6;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    font-size: 14px;
  }
  
  .control-select:focus {
    outline: none;
    border-color: #007bff;
  }
  
  /* Image Grid */
  .image-grid {
    display: grid;
    margin-bottom: 40px;
  }
  
  /* Grid View */
  .image-grid.grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
  
  /* Masonry View */
  .image-grid.masonry {
    grid-auto-rows: 0;
    grid-template-rows: masonry;
  }
  
  /* Responsive columns */
  .image-grid.cols-2 {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .image-grid.cols-3 {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .image-grid.cols-4 {
    grid-template-columns: repeat(4, 1fr);
  }
  
  .image-grid.cols-5 {
    grid-template-columns: repeat(5, 1fr);
  }
  
  .image-grid.cols-6 {
    grid-template-columns: repeat(6, 1fr);
  }
  
  /* Image Item */
  .image-item {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.3s ease;
  }
  
  .image-item:hover {
    transform: translateY(-4px);
  }
  
  .image-container {
    position: relative;
    width: 100%;
    aspect-ratio: 1;
    overflow: hidden;
    background: #f8f9fa;
  }
  
  .image-grid.masonry .image-container {
    aspect-ratio: auto;
    height: auto;
  }
  
  .gallery-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  .image-grid.masonry .gallery-image {
    height: auto;
    max-height: 100%;
  }
  
  .image-item:hover .gallery-image {
    transform: scale(1.05);
  }
  
  /* Loading State */
  .image-loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
  }
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e0e0e0;
    border-top-color: #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  /* Error State */
  .image-error {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
  }
  
  /* Hover Overlay */
  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  .image-item:hover .image-overlay {
    opacity: 1;
  }
  
  .view-btn {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 2px solid white;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .view-btn:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
  
  /* Empty State */
  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #6c757d;
  }
  
  .empty-state h3 {
    margin: 16px 0 0 0;
    font-size: 20px;
    font-weight: 600;
  }
  
  /* Lightbox */
  .lightbox {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
  }
  
  .lightbox-container {
    position: relative;
    width: 100%;
    max-width: 1200px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  
  .lightbox-close {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: background 0.3s ease;
  }
  
  .lightbox-close:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  .lightbox-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 60px;
    height: 60px;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.3s ease;
    z-index: 10;
  }
  
  .lightbox-nav:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
  }
  
  .lightbox-nav:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  .lightbox-nav.prev {
    left: 20px;
  }
  
  .lightbox-nav.next {
    right: 20px;
  }
  
  .lightbox-image-container {
    position: relative;
    width: 100%;
    height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .lightbox-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  
  .lightbox-loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
  }
  
  .image-counter {
    position: absolute;
    top: 20px;
    left: 20px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
  }
  
  .download-btn {
    position: absolute;
    bottom: 20px;
    right: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 10;
  }
  
  .download-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  /* Animations */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
  
  /* Responsive Design */
  @media (max-width: 1200px) {
    .image-grid.cols-6 {
      grid-template-columns: repeat(5, 1fr);
    }
  }
  
  @media (max-width: 992px) {
    .gallery-controls {
      flex-direction: column;
      gap: 16px;
      align-items: stretch;
    }
    
    .control-group {
      justify-content: center;
    }
    
    .image-grid.cols-5,
    .image-grid.cols-6 {
      grid-template-columns: repeat(4, 1fr);
    }
    
    .lightbox-nav {
      width: 50px;
      height: 50px;
    }
  }
  
  @media (max-width: 768px) {
    .image-grid.grid,
    .image-grid.masonry,
    .image-grid.cols-4,
    .image-grid.cols-5,
    .image-grid.cols-6 {
      grid-template-columns: repeat(3, 1fr);
    }
    
    .control-btn span {
      display: none;
    }
    
    .lightbox-image-container {
      height: 60vh;
    }
  }
  
  @media (max-width: 576px) {
    .image-grid.grid,
    .image-grid.masonry,
    .image-grid.cols-3,
    .image-grid.cols-4,
    .image-grid.cols-5,
    .image-grid.cols-6 {
      grid-template-columns: repeat(2, 1fr);
    }
    
    .gallery-controls {
      padding: 12px;
    }
    
    .lightbox-nav {
      width: 40px;
      height: 40px;
    }
    
    .download-btn {
      padding: 8px 16px;
      font-size: 12px;
    }
  }
  
  @media (max-width: 400px) {
    .image-grid.grid,
    .image-grid.masonry,
    .image-grid.cols-2,
    .image-grid.cols-3,
    .image-grid.cols-4,
    .image-grid.cols-5,
    .image-grid.cols-6 {
      grid-template-columns: 1fr;
    }
  }
  </style>